// deps
import React, { memo, useCallback } from "react";

// internal deps
import { IRemovePlanMessage, SavingsLineItemData } from "@swiggy-private/cart-types";
import { ErrorBoundary } from "@swiggy-private/core-ui";
import { SAVINGS_CORNER_TEXT_CONTAINER, SAVINGS_V2_CONTAINER } from "@swiggy-private/cart-test-ids";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";
import { getNRErrorInfo } from "@swiggy-private/js-utils";

// components
import { SavingsLineItem } from "./components/savings-line-item";

// styles
import { BorderDiv, CardContainerWrapper, TextWrapper } from "./SavingsUIRenderer.style";

// constants
import { SAVINGS_CORNER } from "@cart-ui/constants";

interface IProps {
    renderingData: SavingsLineItemData[];
    removePlanMessage: IRemovePlanMessage;
    hideSectionTitle: boolean;
}

const SavingsUIRenderer = ({ renderingData, removePlanMessage, hideSectionTitle }: IProps) => {
    const onError = useCallback((error?: Error, errorInfo?: React.ErrorInfo) => {
        const errorEvents = getNRErrorInfo(error, errorInfo);

        recordNREvent({
            eventType: NEWRELIC_TABLES.CHECKOUT_WEB_ERROR_BOUNDARY,
            widgetType: "SAVINGS_SECTION_RENDERER",
            ...errorEvents,
        });
    }, []);

    return (
        <ErrorBoundary errorFallback={onError}>
            <CardContainerWrapper cornerRadius="medium" data-testid={SAVINGS_V2_CONTAINER}>
                {!hideSectionTitle && (
                    <TextWrapper
                        asElement="h2"
                        variant="V2.overline.neutral.bold_spaced"
                        color="text_Low_Emphasis"
                        data-testid={SAVINGS_CORNER_TEXT_CONTAINER}
                        aria-label={SAVINGS_CORNER}
                    >
                        {SAVINGS_CORNER}
                    </TextWrapper>
                )}

                {renderingData.map((itemData, index) => {
                    const key = itemData.title.value ?? itemData.subtitle?.value ?? index;

                    return (
                        <React.Fragment key={key}>
                            <SavingsLineItem removePlanMessage={removePlanMessage} SavingsLineItemData={itemData} />
                            {index !== renderingData.length - 1 && <BorderDiv />}
                        </React.Fragment>
                    );
                })}
            </CardContainerWrapper>
        </ErrorBoundary>
    );
};

export default memo(SavingsUIRenderer);
